import { addWeeks, getISOWeek, isAfter, isBefore } from 'date-fns'
import { endOfISOWeek, startOfISOWeek } from 'date-fns/esm'

import { type DateK, Month } from '@/dto'
import dateUtils from '@/utils/DateUtils.ts'

export class Week implements DateK {
  readonly #date: Date

  constructor(...args: ConstructorParameters<typeof Date>) {
    this.#date = new Date(...args)
  }

  get date() {
    return endOfISOWeek(this.#date)
  }

  get toISOWeek() {
    return getISOWeek(this.date)
  }

  get toString() {
    return dateUtils.toDateString(this.date, '-')
  }

  get startOf() {
    return startOfISOWeek(this.date)
  }

  get endOf() {
    return endOfISOWeek(this.date)
  }

  static fromDate(date: Date) {
    return new Week(date)
  }

  static getWeekRange(month: Month): Week[]

  static getWeekRange(startDateK: DateK, endDateK: DateK): Week[]

  static getWeekRange(startDateKOrMonth: DateK | Month, endDateK?: DateK): Week[] {
    const startDate =
      startDateKOrMonth instanceof Month ? startDateKOrMonth.startOf : startDateKOrMonth.date

    const endDate = startDateKOrMonth instanceof Month ? startDateKOrMonth.endOf : endDateK?.date

    if (!endDate) throw Error('날짜 형식이 잘못되었습니다.')

    const range: Week[] = []
    const end = endOfISOWeek(endDate)

    if (startDate > endDate) return []

    let currentDate = endOfISOWeek(startDate)

    while (currentDate <= end) {
      range.push(Week.fromDate(currentDate))
      currentDate = addWeeks(currentDate, 1)
    }

    return range
  }

  copy() {
    return Week.fromDate(new Date(this.date))
  }

  isAfter({ date }: DateK) {
    return isAfter(this.endOf, date)
  }

  isBefore({ date }: DateK) {
    return isBefore(this.startOf, date)
  }
}
